import type { GraphQLError } from 'graphql';
import { z } from 'zod/v4';

export type GQLResponse<T = unknown> = GQLResponseSuccess<T> | GQLResponseError;

export interface GQLResponseSuccess<T> {
  data: T;
  error: null;
}

export interface GQLResponseError {
  data: null;
  error: GraphQLError[] | ErrorPayload;
}

export interface ResponseError {
  error?: Error | Error[] | ErrorPayload | null;
}

export interface ErrorPayload {
  error: string;
  status: number;
  message: string;
}

/**
 * What the patched `@nhost/graphql-js` returns when its fetch threw: the originating error plus
 * its errno. Which field carries the errno depends on the
 * transport, so both are optional: node-fetch sets `code`, undici sets it on `cause`.
 *
 * Declared separately because adding an optional member to `ErrorPayload` collapses the
 * narrowing that `throwIfError` performs below, at every call site.
 */
export interface TransportErrorPayload extends ErrorPayload {
  cause?: unknown;
  code?: string;
  type?: string;
}

export const graphqlErrorSchema = z.object({
  message: z.string(),
  extensions: z.object({
    code: z.string(),
  }),
});

export type GraphqlErrorWithCode = z.infer<typeof graphqlErrorSchema>;

export function getResponseDataOrThrow<T>(response: GQLResponse<T>): T {
  throwIfError(response);
  return response.data;
}

type RequiredNonNullable<T> = { [K in keyof T]-?: NonNullable<T[K]> };

export function throwIfError<R extends ResponseError>(
  response: R,
  //@ts-expect-error This is correct but TS does not get it
): asserts response is RequiredNonNullable<Omit<R, keyof ResponseError>> {
  if (response.error) {
    throw toError(response.error);
  }
}

export const isGraphqlError = (error: unknown): error is [GraphQLError, ...GraphQLError[]] => {
  if (error instanceof Array) {
    if ('extensions' in error[0]) {
      return true;
    }
  }

  return false;
};

export function getGraphqlErrorByCode(error: unknown, code: string): GraphqlErrorWithCode | null {
  for (const candidate of toGraphqlErrors(error)) {
    if (candidate.extensions.code === code) {
      return candidate;
    }
  }

  return null;
}

export function getHasuraPermissionError(error: unknown): GraphqlErrorWithCode | null {
  return getGraphqlErrorByCode(error, 'permission-error');
}

export function toError(input: unknown): Error {
  if (Array.isArray(input) && input.length > 0) {
    const errors = input.map(toError);
    return new AggregateError(errors, errors.map((e, i) => `#${i + 1}: ${e.message}`).join('\n\n'));
  }

  if (input instanceof Error) {
    return input;
  }

  if (typeof input === 'string') {
    return new Error(input);
  }

  if (isErrorLike(input)) {
    return Object.assign(new Error(), input);
  }

  try {
    return new Error(JSON.stringify(input));
  } catch {
    return new Error(String(input));
  }
}

function toGraphqlErrors(input: unknown): GraphqlErrorWithCode[] {
  if (input instanceof AggregateError) {
    return input.errors.flatMap((error) => toGraphqlErrors(error));
  }

  if (Array.isArray(input)) {
    return input.flatMap((error) => toGraphqlErrors(error));
  }

  const parseResult = graphqlErrorSchema.safeParse(input);
  return parseResult.success ? [parseResult.data] : [];
}

function isErrorLike(input: unknown): input is { message: string } {
  return (
    typeof input === 'object' &&
    input !== null &&
    'message' in input &&
    typeof input.message === 'string'
  );
}

export function isAbortError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const { name, message } = error as { name?: unknown; message?: unknown };
  return name === 'AbortError' || message === 'AbortError' || message === 'Aborted';
}

export type IgnoredResult<T> = T extends void ? T : T | undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ignoreAbortError<F extends (...args: any[]) => Promise<any>>(fn: F) {
  return (...args: Parameters<F>): Promise<IgnoredResult<Awaited<ReturnType<F>>>> =>
    fn(...args).catch((e) => {
      if (isAbortError(e)) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject(e);
    }) as Promise<IgnoredResult<Awaited<ReturnType<F>>>>;
}
